import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDemo } from '../../contexts/DemoContext';
import {
  Target,
  Users,
  Building2,
  Code2,
  LogOut,
  Menu,
  X,
  Home,
  Briefcase,
  FlaskConical
} from 'lucide-react';
import { useState } from 'react';

const Layout = () => {
  const { user, signOut, isManager, isSuperAdmin, isDeveloper } = useAuth();
  const { isDemoMode, toggleDemoMode } = useDemo();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Show demo mode toggle for admin+ users
  const canAccessDemo = isManager || isSuperAdmin || isDeveloper;

  const navItems = [
    { path: '/dashboard', label: 'My Goals', icon: Target, show: true },
    { path: '/manager', label: 'Team Goals', icon: Users, show: isManager },
    { path: '/company', label: 'Company Goals', icon: Building2, show: isSuperAdmin },
    { path: '/departmental', label: 'Dept Goals', icon: Briefcase, show: isSuperAdmin },
    { path: '/developer', label: 'Developer', icon: Code2, show: isDeveloper },
  ].filter(item => item.show);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="bg-amber-500 text-amber-900 text-center py-2 px-4 font-medium">
          <FlaskConical className="w-4 h-4 inline-block mr-2" />
          Demo Mode Active - Using sample data for training purposes
        </div>
      )}

      <header className="bg-cps-dark shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/select-role')}
                className="p-2 hover:bg-cps-primary/20 rounded-lg transition-colors"
                title="Role Selection"
              >
                <Home className="w-5 h-5 text-white" />
              </button>
              <div className="hidden sm:flex items-center gap-2">
                <Target className="w-6 h-6 text-cps-primary" />
                <span className="font-semibold text-white">CPS Goals</span>
              </div>
            </div>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map(item => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? 'bg-cps-primary text-white'
                      : 'text-gray-200 hover:bg-cps-primary/20'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              {/* Demo Mode Toggle */}
              {canAccessDemo && (
                <button
                  onClick={toggleDemoMode}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    isDemoMode
                      ? 'bg-amber-500 text-amber-900'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  title={isDemoMode ? 'Exit Demo Mode' : 'Enter Demo Mode'}
                >
                  <FlaskConical className="w-4 h-4" />
                  <span className="hidden lg:inline">{isDemoMode ? 'Exit Demo' : 'Demo Mode'}</span>
                </button>
              )}

              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-white">{user?.displayName}</p>
                <p className="text-xs text-gray-300 capitalize">{user?.role}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-300 hover:text-white hover:bg-cps-primary/20 rounded-lg transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-5 h-5" />
              </button>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-300 hover:text-white hover:bg-cps-primary/20 rounded-lg transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t border-cps-primary/30">
              {navItems.map(item => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-2 w-full px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? 'bg-cps-primary text-white'
                      : 'text-gray-200 hover:bg-cps-primary/20'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              ))}

              {/* Demo toggle in mobile menu */}
              {canAccessDemo && (
                <button
                  onClick={() => {
                    toggleDemoMode();
                    setMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-2 w-full px-4 py-3 rounded-lg transition-colors mt-2 ${
                    isDemoMode
                      ? 'bg-amber-500 text-amber-900'
                      : 'text-gray-200 hover:bg-cps-primary/20'
                  }`}
                >
                  <FlaskConical className="w-5 h-5" />
                  <span>{isDemoMode ? 'Exit Demo Mode' : 'Enter Demo Mode'}</span>
                </button>
              )}
            </nav>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;